import { getOrCreateTooltip } from '@/components/LineChart/helpers/getOrCreateTooltip';

import type { Chart, TooltipModel } from 'chart.js';
import type { TFunction } from 'i18next';

export const externalTooltipHandler = (
  context: { chart: Chart; tooltip: TooltipModel<'line'> },
  t: TFunction,
  tooltipTopPos: number,
  lineChartWidth: number
) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  if (!tooltipEl) {
    return;
  }

  // Now that verticalIndicator exists, lets get it
  const verticalIndicator = chart.canvas.parentNode?.querySelector(
    'span[data-indicator="chart"]'
  ) as HTMLSpanElement | null;

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    if (verticalIndicator) {
      verticalIndicator.style.opacity = '0';
    }
    return;
  }

  // Set Text
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map((b) => b.lines[0]);

    const tableHead = document.createElement('thead');
    const tableBody = document.createElement('tbody');

    // On rempli et stylise la partie "montant" de la tooltip
    bodyLines.forEach((montant: string) => {
      const tr = document.createElement('tr');
      tr.style.backgroundColor = 'inherit';
      tr.style.borderWidth = '0';
      tr.style.lineHeight = '10px';

      const th = document.createElement('td');
      th.style.borderWidth = '0';
      th.style.fontSize = '16px';
      th.style.fontWeight = '700';
      th.style.color = '#040404';

      const numericValue = Number.parseFloat(montant.replaceAll(/[^\d.,-]/g, '').replace(',', '.'));
      const text = document.createTextNode(t('format.currencyFlexible', { value: numericValue }));

      th.appendChild(text);
      tr.appendChild(th);
      tableHead.appendChild(tr);
    });

    // On rempli et stylise la partie "date" de la tooltip
    titleLines.forEach((date: string) => {
      const tr = document.createElement('tr');
      tr.style.borderWidth = '0';

      const td = document.createElement('th');
      td.style.borderWidth = '0';
      td.style.fontSize = '12px';
      td.style.fontWeight = '400';
      td.style.color = '#7F7F7F';
      const text = document.createTextNode(date.split('-').reverse().join('/'));
      text.textContent = 'au ' + text.textContent;
      td.appendChild(text);
      tr.appendChild(td);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector('table');

    // Remove old children safely
    if (tableRoot) {
      tableRoot.innerHTML = '';

      // Add new children
      tableRoot.appendChild(tableHead);
      tableRoot.appendChild(tableBody);
    }
  }

  // Récupérer l'offsetX de la chart
  const { offsetLeft: positionX } = chart.canvas;

  // Prevent text from wrapping
  tooltipEl.style.whiteSpace = 'nowrap';

  // Display, position, and set styles for font
  if (verticalIndicator) {
    verticalIndicator.style.opacity = '1';
    verticalIndicator.style.left = `${positionX + tooltip.caretX}px`;
    verticalIndicator.style.top = '5px';
  }

  tooltipEl.style.opacity = '1';

  tooltipEl.style.left =
    Math.max(tooltipEl.offsetWidth / 2, Math.min(tooltip.caretX, lineChartWidth - tooltipEl.offsetWidth / 2)) + 'px';

  tooltipEl.style.top = `${(tooltip.caretY ?? 0) - tooltipTopPos}px`;

  // Handle font properly
  const bodyFont = tooltip.options.bodyFont;
  if (bodyFont && typeof bodyFont === 'object' && 'string' in bodyFont) {
    tooltipEl.style.font = bodyFont.string as string;
  }

  // Handle padding properly
  const tooltipPadding = tooltip.options.padding;
  const paddingValue = typeof tooltipPadding === 'number' ? tooltipPadding : 0;
  tooltipEl.style.padding = `${paddingValue}px ${paddingValue}px 0px ${paddingValue}px`;
};
